<script lang="ts">
  import { 
    X, CreditCard, Building, ShieldCheck, CheckCircle2, 
    AlertCircle, Trash2, Plus, Calendar, Bell, DollarSign,
    Sparkles, ArrowRight, Lock
  } from 'lucide-svelte';
  import { onMount } from 'svelte';

  interface PaymentMethod {
    payment_method_id: string;
    method_type: 'card' | 'ach';
    card_brand?: string;
    last4: string;
    exp_month?: number;
    exp_year?: number;
    bank_name?: string;
    account_type?: string;
    is_default: boolean;
    billing_name: string;
    billing_zip?: string;
  }

  interface AutopaySettings {
    setting_id: string;
    is_enabled: boolean;
    payment_method_id?: string;
    debit_day: number;
    max_limit_usd: number;
    notify_email: boolean;
    notify_sms: boolean;
    last_debited_at?: string;
    last_debited_amount_usd?: number;
    payment_method_summary?: string;
  }

  let { 
    open = $bindable(false),
    propertyId = 700,
    unitId = '',
    unitLabel = 'Your Unit'
  } = $props<{
    open: boolean;
    propertyId?: number;
    unitId?: string;
    unitLabel?: string;
  }>();

  let paymentMethods = $state<PaymentMethod[]>([]);
  let autopay = $state<AutopaySettings | null>(null);
  let loading = $state(true);
  let saving = $state(false);
  let feedback = $state('');
  let feedbackType = $state<'success' | 'error'>('success');

  // Add Payment Method Form State
  let showAddForm = $state(false);
  let newType = $state<'card' | 'ach'>('card');
  let newName = $state('Resident Primary');
  let newNumber = $state('');
  let newExp = $state('12/28');
  let newCVV = $state('123');
  let newZip = $state('90012');
  let newBankName = $state('Chase Premier Checking');
  let newRouting = $state('122000496');
  let newAccountNum = $state('9876543210');
  let newAccountType = $state<'checking' | 'savings'>('checking');
  let makeDefault = $state(true);

  onMount(() => {
    loadData();
  });

  $effect(() => {
    if (open) {
      loadData();
    }
  });

  async function loadData() {
    loading = true;
    feedback = '';
    try {
      const pmRes = await fetch(`/api/properties/${propertyId}/payment-methods?unit_id=${encodeURIComponent(unitId)}`);
      if (pmRes.ok) {
        const pmData = await pmRes.json();
        paymentMethods = pmData.payment_methods || [];
      }

      const autoRes = await fetch(`/api/properties/${propertyId}/autopay?unit_id=${encodeURIComponent(unitId)}`);
      if (autoRes.ok) {
        autopay = await autoRes.json();
      }
    } catch (e: any) {
      feedback = 'Error loading autopay: ' + (e.message || 'Network error');
      feedbackType = 'error';
    } finally {
      loading = false;
    }
  }

  async function saveAutopay() {
    if (!autopay) return;
    saving = true;
    feedback = '';
    try {
      const res = await fetch(`/api/properties/${propertyId}/autopay?unit_id=${encodeURIComponent(unitId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(autopay)
      });
      if (!res.ok) throw new Error('Failed to update autopay settings');
      feedback = 'Autopay preferences saved successfully!';
      feedbackType = 'success';
      setTimeout(() => feedback = '', 3000);
    } catch (e: any) {
      feedback = e.message || 'Error saving autopay';
      feedbackType = 'error';
    } finally {
      saving = false;
    }
  }

  async function handleAddPaymentMethod() {
    saving = true;
    feedback = '';
    try {
      let payload: Partial<PaymentMethod> = {
        method_type: newType,
        billing_name: newName,
        billing_zip: newZip,
        is_default: makeDefault
      };

      if (newType === 'card') {
        const last4 = newNumber.replace(/\D/g, '').slice(-4) || '8888';
        const [mStr, yStr] = newExp.split('/');
        const expMonth = parseInt(mStr) || 12;
        const expYear = 2000 + (parseInt(yStr) || 28);
        payload = {
          ...payload,
          card_brand: 'visa',
          last4,
          exp_month: expMonth,
          exp_year: expYear
        };
      } else {
        const last4 = newAccountNum.slice(-4) || '3210';
        payload = {
          ...payload,
          bank_name: newBankName,
          account_type: newAccountType,
          last4
        };
      }

      const res = await fetch(`/api/properties/${propertyId}/payment-methods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to save payment method');

      showAddForm = false;
      feedback = 'Payment method added to vault!';
      feedbackType = 'success';
      await loadData();
    } catch (e: any) {
      feedback = e.message || 'Error adding payment method';
      feedbackType = 'error';
    } finally {
      saving = false;
    }
  }

  async function setDefaultMethod(pmId: string) {
    try {
      const res = await fetch(`/api/properties/${propertyId}/payment-methods/${pmId}/default?unit_id=${encodeURIComponent(unitId)}`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Failed to set default method');
      await loadData();
    } catch (e: any) {
      feedback = e.message;
      feedbackType = 'error';
    }
  }

  async function deleteMethod(pmId: string) {
    if (!confirm('Remove this payment method from your resident account?')) return;
    try {
      const res = await fetch(`/api/properties/${propertyId}/payment-methods/${pmId}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Failed to delete payment method');
      await loadData();
    } catch (e: any) {
      feedback = e.message;
      feedbackType = 'error';
    }
  }
</script>

{#if open}
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
    <div class="bg-base-100 border border-base-200 rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh]">
      
      <!-- Modal Header -->
      <div class="px-6 py-5 border-b border-base-200 bg-base-200/50 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-bold shadow-sm">
            <Lock class="w-5 h-5" />
          </div>
          <div>
            <h3 class="font-extrabold text-base-content text-base leading-tight flex items-center gap-2">
              Resident Autopay & Payment Vault
              <span class="badge badge-xs badge-primary font-bold uppercase">PCI-DSS Level 1</span>
            </h3>
            <p class="text-xs text-base-content/60">Automated submetered billing for Suite {unitLabel}</p>
          </div>
        </div>

        <button 
          class="btn btn-sm btn-ghost btn-circle text-base-content/50 hover:text-base-content"
          onclick={() => open = false}
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Feedback Alert -->
      {#if feedback}
        <div class="mx-6 mt-4 p-3 rounded-2xl text-xs font-semibold flex items-center gap-2 {feedbackType === 'success' ? 'bg-success/10 text-success border border-success/20' : 'bg-error/10 text-error border border-error/20'}">
          {#if feedbackType === 'success'}
            <CheckCircle2 class="w-4 h-4 shrink-0" />
          {:else}
            <AlertCircle class="w-4 h-4 shrink-0" />
          {/if}
          <span>{feedback}</span>
        </div>
      {/if}

      <!-- Modal Body -->
      <div class="p-6 space-y-6 overflow-y-auto">
        
        <!-- Autopay Main Toggle Card -->
        {#if autopay}
          <div class="p-5 rounded-2xl border transition-all {autopay.is_enabled ? 'bg-gradient-to-r from-emerald-500/10 via-primary/5 to-transparent border-emerald-500/30' : 'bg-base-200/40 border-base-200'}">
            <div class="flex items-center justify-between gap-4">
              <div class="flex items-center gap-3.5">
                <div class="w-12 h-12 rounded-2xl flex items-center justify-center font-bold {autopay.is_enabled ? 'bg-emerald-500 text-white shadow-md' : 'bg-base-300 text-base-content/40'}">
                  <ShieldCheck class="w-6 h-6" />
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <h4 class="font-extrabold text-sm text-base-content">Automated Monthly Autopay</h4>
                    <span class="badge badge-sm font-black {autopay.is_enabled ? 'badge-success text-white' : 'badge-ghost text-base-content/50'}">
                      {autopay.is_enabled ? 'ACTIVE' : 'PAUSED'}
                    </span>
                  </div>
                  <p class="text-xs text-base-content/60 mt-0.5">
                    Automatically debit your default payment method on billing cycle close. Never incur late fees.
                  </p>
                </div>
              </div>

              <!-- Switch -->
              <input 
                type="checkbox" 
                bind:checked={autopay.is_enabled}
                class="toggle toggle-primary toggle-lg shadow-sm"
              />
            </div>

            {#if autopay.is_enabled}
              <div class="mt-4 pt-4 border-t border-base-content/10 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <!-- Debit Day -->
                <div class="form-control">
                  <label class="label py-0.5 text-xs font-bold text-base-content/70">
                    <span class="flex items-center gap-1.5"><Calendar class="w-3.5 h-3.5 text-primary" /> Monthly Debit Day</span>
                  </label>
                  <select 
                    bind:value={autopay.debit_day}
                    class="select select-sm select-bordered rounded-xl bg-base-100 font-semibold"
                  >
                    <option value={1}>1st of every month (Default)</option>
                    <option value={5}>5th of every month</option>
                    <option value={10}>10th of every month</option>
                    <option value={15}>15th of every month</option>
                  </select>
                </div>

                <!-- Maximum Debit Limit -->
                <div class="form-control">
                  <label class="label py-0.5 text-xs font-bold text-base-content/70">
                    <span class="flex items-center gap-1.5"><DollarSign class="w-3.5 h-3.5 text-primary" /> Safety Budget Cap</span>
                  </label>
                  <div class="relative">
                    <span class="absolute left-3 top-2 text-base-content/50 font-bold">$</span>
                    <input 
                      type="number" 
                      bind:value={autopay.max_limit_usd} 
                      step="25" 
                      class="input input-sm input-bordered rounded-xl pl-7 w-full font-mono font-bold"
                    />
                  </div>
                  <span class="text-[10px] text-base-content/50 mt-1">Transactions exceeding cap require manual review.</span>
                </div>
              </div>

              <!-- Notification Checkboxes -->
              <div class="mt-3 flex flex-wrap gap-4 pt-2">
                <label class="flex items-center gap-2 cursor-pointer text-xs font-medium text-base-content/80">
                  <input type="checkbox" bind:checked={autopay.notify_email} class="checkbox checkbox-xs checkbox-primary rounded" />
                  <span>Send receipt via Email</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer text-xs font-medium text-base-content/80">
                  <input type="checkbox" bind:checked={autopay.notify_sms} class="checkbox checkbox-xs checkbox-primary rounded" />
                  <span>Send SMS payment confirmation</span>
                </label>
              </div>
            {/if}
          </div>
        {/if}

        <!-- Saved Payment Methods Section -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <h4 class="text-xs font-bold uppercase tracking-wider text-base-content/70">
              Saved Payment Methods ({paymentMethods.length})
            </h4>

            {#if !showAddForm}
              <button 
                onclick={() => showAddForm = true}
                class="btn btn-xs btn-outline btn-primary gap-1 rounded-xl font-bold"
              >
                <Plus class="w-3.5 h-3.5" />
                Add Payment Method
              </button>
            {/if}
          </div>

          <!-- Add Method Form Drawer -->
          {#if showAddForm}
            <div class="p-4 rounded-2xl bg-base-200 border border-primary/20 space-y-4 animate-fade-in">
              <div class="flex items-center justify-between">
                <span class="text-xs font-black uppercase tracking-wider text-primary">New Payment Method</span>
                <button 
                  onclick={() => showAddForm = false}
                  class="btn btn-xs btn-ghost btn-circle"
                >
                  <X class="w-3.5 h-3.5" />
                </button>
              </div>

              <!-- Method Type Picker -->
              <div class="grid grid-cols-2 gap-2">
                <button 
                  class="btn btn-sm rounded-xl font-bold gap-2 {newType === 'card' ? 'btn-primary text-white' : 'btn-ghost border border-base-300'}"
                  onclick={() => newType = 'card'}
                >
                  <CreditCard class="w-4 h-4" /> Credit / Debit Card
                </button>
                <button 
                  class="btn btn-sm rounded-xl font-bold gap-2 {newType === 'ach' ? 'btn-primary text-white' : 'btn-ghost border border-base-300'}"
                  onclick={() => newType = 'ach'}
                >
                  <Building class="w-4 h-4" /> ACH Direct Debit
                </button>
              </div>

              <!-- Card Fields -->
              {#if newType === 'card'}
                <div class="space-y-3 text-xs">
                  <div class="form-control">
                    <label class="label py-0.5 font-bold text-base-content/70">Name on Card</label>
                    <input type="text" bind:value={newName} class="input input-sm input-bordered rounded-xl" placeholder="Full legal name" />
                  </div>
                  <div class="form-control">
                    <label class="label py-0.5 font-bold text-base-content/70">Card Number</label>
                    <input type="text" bind:value={newNumber} class="input input-sm input-bordered rounded-xl font-mono" placeholder="•••• •••• •••• 4242" />
                  </div>
                  <div class="grid grid-cols-3 gap-2">
                    <div class="form-control">
                      <label class="label py-0.5 font-bold text-base-content/70">Expires</label>
                      <input type="text" bind:value={newExp} class="input input-sm input-bordered rounded-xl font-mono" placeholder="MM/YY" />
                    </div>
                    <div class="form-control">
                      <label class="label py-0.5 font-bold text-base-content/70">CVV</label>
                      <input type="password" bind:value={newCVV} class="input input-sm input-bordered rounded-xl font-mono" placeholder="•••" maxlength="4" />
                    </div>
                    <div class="form-control">
                      <label class="label py-0.5 font-bold text-base-content/70">ZIP Code</label>
                      <input type="text" bind:value={newZip} class="input input-sm input-bordered rounded-xl font-mono" placeholder="90012" />
                    </div>
                  </div>
                </div>
              {:else}
                <!-- ACH Fields -->
                <div class="space-y-3 text-xs">
                  <div class="form-control">
                    <label class="label py-0.5 font-bold text-base-content/70">Account Holder Name</label>
                    <input type="text" bind:value={newName} class="input input-sm input-bordered rounded-xl" placeholder="Name on bank account" />
                  </div>
                  <div class="grid grid-cols-2 gap-2">
                    <div class="form-control">
                      <label class="label py-0.5 font-bold text-base-content/70">Bank Institution</label>
                      <input type="text" bind:value={newBankName} class="input input-sm input-bordered rounded-xl" placeholder="Chase, BofA, Wells Fargo" />
                    </div>
                    <div class="form-control">
                      <label class="label py-0.5 font-bold text-base-content/70">Account Type</label>
                      <select bind:value={newAccountType} class="select select-sm select-bordered rounded-xl">
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                      </select>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-2">
                    <div class="form-control">
                      <label class="label py-0.5 font-bold text-base-content/70">Routing Number (ABA)</label>
                      <input type="text" bind:value={newRouting} class="input input-sm input-bordered rounded-xl font-mono" placeholder="9 digits" />
                    </div>
                    <div class="form-control">
                      <label class="label py-0.5 font-bold text-base-content/70">Account Number</label>
                      <input type="password" bind:value={newAccountNum} class="input input-sm input-bordered rounded-xl font-mono" placeholder="Account #" />
                    </div>
                  </div>
                </div>
              {/if}

              <div class="flex items-center justify-between pt-2">
                <label class="flex items-center gap-2 cursor-pointer text-xs font-bold text-base-content/80">
                  <input type="checkbox" bind:checked={makeDefault} class="checkbox checkbox-xs checkbox-primary rounded" />
                  <span>Set as default autopay method</span>
                </label>

                <div class="flex gap-2">
                  <button onclick={() => showAddForm = false} class="btn btn-xs btn-ghost">Cancel</button>
                  <button 
                    onclick={handleAddPaymentMethod} 
                    class="btn btn-xs btn-primary font-bold text-white px-4"
                    disabled={saving}
                  >
                    {saving ? 'Encrypting & Saving...' : 'Save Method'}
                  </button>
                </div>
              </div>
            </div>
          {/if}

          <!-- Method Items List -->
          <div class="space-y-2">
            {#each paymentMethods as m}
              <div class="p-4 rounded-2xl bg-base-200/50 border border-base-200 flex items-center justify-between gap-4">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-xl bg-base-100 border border-base-300 flex items-center justify-center text-primary font-bold shadow-xs">
                    {#if m.method_type === 'ach'}
                      <Building class="w-5 h-5 text-indigo-500" />
                    {:else}
                      <CreditCard class="w-5 h-5 text-primary" />
                    {/if}
                  </div>
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-extrabold text-sm text-base-content">
                        {m.method_type === 'ach' ? (m.bankName || 'ACH Bank Transfer') : `${(m.card_brand || 'Card').toUpperCase()} •••• ${m.last4}`}
                      </span>
                      {#if m.is_default}
                        <span class="badge badge-xs badge-success text-white font-bold uppercase">Default</span>
                      {/if}
                    </div>
                    <p class="text-xs text-base-content/50 mt-0.5">
                      {m.billing_name} {#if m.exp_month}• Exp {m.exp_month}/{m.exp_year}{/if}
                    </p>
                  </div>
                </div>

                <div class="flex items-center gap-2">
                  {#if !m.is_default}
                    <button 
                      onclick={() => setDefaultMethod(m.payment_method_id)}
                      class="btn btn-xs btn-ghost text-xs text-primary font-bold hover:underline"
                    >
                      Make Default
                    </button>
                  {/if}
                  <button 
                    onclick={() => deleteMethod(m.payment_method_id)}
                    class="btn btn-xs btn-ghost btn-square text-error/60 hover:text-error hover:bg-error/10"
                    title="Delete payment method"
                  >
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>
              </div>
            {/each}
          </div>
        </div>

      </div>

      <!-- Modal Footer -->
      <div class="px-6 py-4 border-t border-base-200 bg-base-200/50 flex items-center justify-between">
        <div class="flex items-center gap-2 text-[11px] text-base-content/50">
          <ShieldCheck class="w-4 h-4 text-emerald-500" />
          <span>AES-256 encrypted tokenization. Numbers never stored plain text.</span>
        </div>

        <div class="flex items-center gap-2">
          <button 
            onclick={() => open = false}
            class="btn btn-sm btn-ghost rounded-xl text-xs font-semibold"
          >
            Close
          </button>
          <button 
            onclick={saveAutopay}
            class="btn btn-sm btn-primary rounded-xl text-xs font-bold text-white px-5 shadow-md active:scale-95"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Autopay Settings'}
          </button>
        </div>
      </div>

    </div>
  </div>
{/if}
